import QRCode from "qrcode";
import {
  ArrowRight,
  Check,
  ChevronLeft,
  Files,
  FolderOpen,
  LayoutList,
  List,
  LockKeyhole,
  LogOut,
  Moon,
  QrCode,
  RefreshCw,
  Send,
  ShieldCheck,
  Smartphone,
  Sun,
  Wifi,
  X,
} from "lucide-solid";
import { Show, createEffect, createSignal, onCleanup, type Component } from "solid-js";
import {
  createRoomCredentials,
  formatBytes,
  safeFileName,
  type CollisionAction,
  type FileDecision,
  type FileOffer,
  type RoomCredentials,
  type TransferManifest,
} from "@kitae/transfer-protocol";
import FileList from "./components/FileList";
import { prepareFiles, type PreparedFile } from "./lib/files";
import { LanPeer, type PeerStatus } from "./lib/peer";
import { SignalingClient, type SignalingStatus } from "./lib/signaling";
import { fileExists, ReceiverTransfer, SenderTransfer, type TransferProgress } from "./lib/transfer";
import { DirectoryMemory, ensureReadWritePermission } from "./lib/directory-memory";
import { saveTheme, type Theme } from "./lib/theme";
import type { PublicConfig } from "./lib/config";

type ReceiverPhase = "idle" | "waiting" | "connected" | "offered" | "receiving" | "done" | "ended" | "error";
type SenderPhase = "connecting" | "selecting" | "preparing" | "ready" | "proposed" | "sending" | "done" | "ended" | "error";

function totalProgress(files: readonly FileOffer[], progress: Readonly<Record<string, number>>): number {
  const total = files.reduce((sum, file) => sum + file.size, 0);
  if (total === 0) return files.length > 0 ? 100 : 0;
  return Math.round(files.reduce((sum, file) => sum + Math.min(file.size, progress[file.id] ?? 0), 0) / total * 100);
}

function renamedFileName(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? `${name.slice(0, dot)} (1)${name.slice(dot)}` : `${name} (1)`;
}

const Brand: Component = () => (
  <a href="/" class="brand" aria-label="Kitae Transfer — accueil">
    <span class="brand-mark"><ArrowRight size={19} stroke-width={2.5} /></span>
    <span>Kitae <strong>Transfer</strong></span>
  </a>
);

const ThemeToggle: Component = () => {
  const [theme, setTheme] = createSignal<Theme>(document.documentElement.dataset.theme === "dark" ? "dark" : "light");
  const toggle = () => {
    const next = theme() === "dark" ? "light" : "dark";
    setTheme(next);
    saveTheme(next);
  };
  return <button class="theme-toggle" onClick={toggle} aria-label={theme() === "dark" ? "Utiliser le thème clair" : "Utiliser le thème sombre"}>
    <Show when={theme() === "dark"} fallback={<Moon size={18} />}><Sun size={18} /></Show>
  </button>;
};

const HeaderActions: Component<{ signaling?: SignalingStatus; peer?: PeerStatus }> = (props) => (
  <div class="topbar-actions">
    <Show when={props.signaling && props.peer}><StatusPill signaling={props.signaling!} peer={props.peer!} /></Show>
    <ThemeToggle />
  </div>
);

const PrivacyStrip: Component = () => (
  <div class="privacy-strip">
    <ShieldCheck size={17} />
    <span>Direct sur votre réseau local</span>
    <span class="privacy-dot" />
    <span>Aucun fichier stocké</span>
  </div>
);

const StatusPill: Component<{ signaling: SignalingStatus; peer: PeerStatus }> = (props) => {
  const label = () => props.peer === "connected" ? "Connexion directe établie" : props.signaling === "reconnecting" || props.peer === "reconnecting" ? "Reconnexion…" : "Connexion sécurisée";
  return <div class="status-pill"><span classList={{ "status-live": props.peer === "connected" }} />{label()}</div>;
};

interface CollisionRequest {
  readonly file: FileOffer;
  readonly suggestedName: string;
  readonly resolve: (choice: { action: CollisionAction; finalName: string } | null) => void;
}

const CollisionDialog: Component<{ request: CollisionRequest }> = (props) => {
  const [name, setName] = createSignal(props.request.suggestedName);
  return (
    <div class="dialog-backdrop" role="presentation">
      <section class="dialog" role="dialog" aria-modal="true" aria-labelledby="collision-title">
        <div class="dialog-icon"><Files size={23} /></div>
        <h2 id="collision-title">Ce fichier existe déjà</h2>
        <p><strong>{props.request.file.name}</strong> est déjà présent dans le dossier choisi.</p>
        <label class="field-label" for="rename-field">Nouveau nom</label>
        <input id="rename-field" class="text-field" value={name()} onInput={(event) => setName(event.currentTarget.value)} />
        <div class="dialog-actions">
          <button class="button ghost" onClick={() => props.request.resolve({ action: "skip", finalName: props.request.file.name })}>Ignorer</button>
          <button class="button secondary" disabled={!safeFileName(name())} onClick={() => props.request.resolve({ action: "rename", finalName: safeFileName(name()) })}>Renommer</button>
          <button class="button danger" onClick={() => props.request.resolve({ action: "replace", finalName: props.request.file.name })}>Remplacer</button>
        </div>
      </section>
    </div>
  );
};

const ReceiverView: Component<{ config: PublicConfig }> = (props) => {
  const supported = typeof window.showDirectoryPicker === "function";
  const [phase, setPhase] = createSignal<ReceiverPhase>("idle");
  const [credentials, setCredentials] = createSignal<RoomCredentials>();
  const [qr, setQr] = createSignal("");
  const [manifest, setManifest] = createSignal<TransferManifest>();
  const [detailed, setDetailed] = createSignal(true);
  const [progress, setProgress] = createSignal<Record<string, number>>({});
  const [error, setError] = createSignal("");
  const [signalingStatus, setSignalingStatus] = createSignal<SignalingStatus>("closed");
  const [peerStatus, setPeerStatus] = createSignal<PeerStatus>("idle");
  const [collision, setCollision] = createSignal<CollisionRequest>();
  const [lastDirectory, setLastDirectory] = createSignal<FileSystemDirectoryHandle>();
  const [folderNotice, setFolderNotice] = createSignal("");
  const directoryMemory = new DirectoryMemory();
  let signaling: SignalingClient | undefined;
  let peer: LanPeer | undefined;
  let transfer: ReceiverTransfer | undefined;

  void directoryMemory.load().then(setLastDirectory);

  const joinUrl = () => {
    const value = credentials();
    return value ? `${location.origin}/join/${value.roomId}#${value.secret}` : "";
  };

  createEffect(() => {
    const url = joinUrl();
    if (url) void QRCode.toDataURL(url, { width: 320, margin: 2, color: { dark: "#17201d", light: "#ffffff" }, errorCorrectionLevel: "M" }).then(setQr);
  });

  onCleanup(() => {
    signaling?.close();
    peer?.closePeer();
  });

  const setTransferProgress = (value: TransferProgress) => {
    setProgress((current) => ({ ...current, [value.fileId]: value.transferred }));
    setPhase("receiving");
  };

  const start = () => {
    if (!supported) return;
    const room = createRoomCredentials();
    setCredentials(room);
    setPhase("waiting");
    transfer = new ReceiverTransfer();
    transfer.onManifest = (value) => { setManifest(value); setProgress({}); setError(""); setFolderNotice(""); setPhase("offered"); };
    transfer.onProgress = setTransferProgress;
    transfer.onComplete = () => setPhase("done");
    transfer.onError = (message) => { setError(message); setPhase("error"); };
    signaling = new SignalingClient(props.config.signalingUrl, room.roomId, room.secret, "receiver");
    peer = new LanPeer("receiver", (payload) => signaling!.send(payload));
    signaling.onStatus = setSignalingStatus;
    signaling.onError = (message) => { setError(message); if (phase() === "waiting") setPhase("error"); };
    signaling.onSignal = (payload) => peer!.handleSignal(payload);
    signaling.onPeerChange = (present) => { if (present) { setPhase((value) => value === "waiting" ? "connected" : value); void peer!.start(); } };
    peer.onStatus = setPeerStatus;
    peer.onSessionEnded = () => { signaling?.close(); setPhase("ended"); };
    peer.onChannel = (channel) => transfer!.attach(channel);
    signaling.connect();
  };

  const askCollision = (file: FileOffer): Promise<{ action: CollisionAction; finalName: string } | null> => new Promise((resolve) => {
    setCollision({ file, suggestedName: renamedFileName(file.name), resolve: (choice) => { setCollision(); resolve(choice); } });
  });

  const acceptInDirectory = async (directory: FileSystemDirectoryHandle) => {
    const currentManifest = manifest();
    if (!currentManifest || !transfer) return;
    try {
      const decisions: FileDecision[] = [];
      for (const file of currentManifest.files) {
        let action: CollisionAction = "replace";
        let finalName = file.name;
        if (await fileExists(directory, finalName)) {
          const choice = await askCollision(file);
          if (!choice) return;
          action = choice.action;
          finalName = choice.finalName;
          while (action === "rename" && await fileExists(directory, finalName)) {
            const next = await askCollision({ ...file, name: finalName });
            if (!next) return;
            action = next.action;
            finalName = next.finalName;
          }
        }
        decisions.push({ id: file.id, action, finalName });
      }
      await transfer.accept(directory, decisions);
      setLastDirectory(directory);
      await directoryMemory.save(directory);
      setFolderNotice("");
      setPhase("receiving");
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === "AbortError") return;
      setFolderNotice(reason instanceof Error ? reason.message : "Le dossier n’a pas pu être ouvert. Le lot reste disponible.");
      setPhase("offered");
    }
  };

  const chooseDirectory = async () => {
    if (!window.showDirectoryPicker) return;
    try {
      let directory: FileSystemDirectoryHandle;
      try {
        directory = await window.showDirectoryPicker({ mode: "readwrite", startIn: lastDirectory() });
      } catch (reason) {
        if (reason instanceof DOMException && reason.name === "AbortError") return;
        directory = await window.showDirectoryPicker({ mode: "readwrite" });
      }
      await acceptInDirectory(directory);
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === "AbortError") return;
      setFolderNotice("Le dossier n’a pas pu être ouvert. Le lot reste disponible.");
    }
  };

  const useLastDirectory = async () => {
    const directory = lastDirectory();
    if (!directory) return;
    if (!await ensureReadWritePermission(directory)) {
      setFolderNotice("L’accès à ce dossier n’est plus autorisé. Choisissez un autre dossier.");
      return;
    }
    await acceptInDirectory(directory);
  };

  const forgetDirectory = async () => {
    await directoryMemory.forget();
    setLastDirectory();
    setFolderNotice("");
  };

  const endSession = async (remote = false) => {
    if (!remote) await peer?.endSession().catch(() => undefined);
    signaling?.close();
    peer?.closePeer();
    if (remote) setPhase("ended");
    else reset();
  };

  const reset = () => {
    signaling?.close();
    peer?.closePeer();
    setCredentials(); setManifest(); setProgress({}); setError(""); setPhase("idle");
  };

  const files = () => manifest()?.files ?? [];
  const overall = () => totalProgress(files(), progress());

  return (
    <main class="page-shell">
      <header class="topbar"><Brand /><HeaderActions signaling={phase() !== "idle" ? signalingStatus() : undefined} peer={phase() !== "idle" ? peerStatus() : undefined} /></header>
      <Show when={phase() === "idle"}>
        <section class="hero-grid">
          <div class="hero-copy">
            <span class="eyebrow"><Wifi size={15} /> Smartphone → PC, sans détour</span>
            <h1>Vos fichiers.<br /><em>Directement ici.</em></h1>
            <p class="hero-lead">Un passage privé entre votre smartphone et ce PC. Les fichiers restent sur votre réseau local — rien n’est envoyé sur nos serveurs.</p>
            <Show when={supported} fallback={
              <div class="unsupported"><X size={20} /><div><strong>Navigateur non compatible</strong><p>Ouvrez cette page dans une version récente de Microsoft Edge ou Google Chrome.</p></div></div>
            }>
              <button class="button primary hero-button" onClick={start}>Créer un transfert <ArrowRight size={19} /></button>
            </Show>
            <div class="trust-row"><span><LockKeyhole size={16} /> Chiffré de bout en bout</span><span><ShieldCheck size={16} /> Sans compte</span><span><Wifi size={16} /> Réseau local</span></div>
          </div>
          <div class="connection-illustration" aria-hidden="true">
            <div class="device phone"><Smartphone size={34} /><span>Smartphone</span></div>
            <div class="signal-path"><i /><i /><i /><span>WebRTC</span></div>
            <div class="device computer"><div class="screen"><ArrowRight size={28} /></div><span>Ce PC</span></div>
          </div>
        </section>
        <section class="steps">
          <article><span>01</span><div><strong>Créez le passage</strong><p>Un QR code privé apparaît sur ce PC.</p></div></article>
          <article><span>02</span><div><strong>Scannez avec le smartphone</strong><p>Choisissez vos photos et documents.</p></div></article>
          <article><span>03</span><div><strong>Acceptez ici</strong><p>Choisissez le dossier, puis recevez.</p></div></article>
        </section>
      </Show>

      <Show when={phase() === "waiting" || phase() === "connected"}>
        <section class="session-card qr-session">
          <button class="back-link" onClick={reset}><ChevronLeft size={17} /> Annuler</button>
          <div class="session-heading"><span class="step-label">Étape 1 sur 2</span><h1>Scannez avec votre smartphone</h1><p>Ouvrez l’appareil photo et visez ce QR code. Gardez cette page ouverte.</p></div>
          <div class="qr-frame"><Show when={qr()} fallback={<RefreshCw class="animate-spin" />}><img src={qr()} alt="QR code privé de la session" /></Show><span class="qr-corner top-left" /><span class="qr-corner top-right" /><span class="qr-corner bottom-left" /><span class="qr-corner bottom-right" /></div>
          <div class="waiting-line"><span class="pulse-dot" />{phase() === "connected" ? "Smartphone connecté — préparation des fichiers…" : "En attente du smartphone…"}</div>
          <details class="manual-link"><summary>Le scan ne fonctionne pas ?</summary><input class="sr-only" aria-label="Lien privé de la session" readOnly value={joinUrl()} /><button onClick={() => void navigator.clipboard.writeText(joinUrl())}>Copier le lien privé</button></details>
        </section>
      </Show>

      <Show when={phase() === "offered" || phase() === "receiving" || phase() === "done"}>
        <section class="session-card transfer-card">
          <div class="session-heading left">
            <span class="step-label">{phase() === "offered" ? "Prêt à recevoir" : phase() === "done" ? "Transfert terminé" : "Réception en cours"}</span>
            <h1>{phase() === "offered" ? "Le smartphone propose ces fichiers" : phase() === "done" ? "Tout est arrivé." : `${overall()} % transféré`}</h1>
            <p>{files().length} fichier{files().length > 1 ? "s" : ""} · {formatBytes(manifest()?.totalSize ?? 0)} · transfert direct sur le LAN</p>
          </div>
          <div class="list-toolbar"><strong>Contenu</strong><div class="density-toggle"><button classList={{ active: !detailed() }} onClick={() => setDetailed(false)} aria-label="Liste compacte"><List size={17} /></button><button classList={{ active: detailed() }} onClick={() => setDetailed(true)} aria-label="Liste avec aperçus"><LayoutList size={17} /></button></div></div>
          <FileList files={files()} detailed={detailed()} progress={progress()} />
          <Show when={phase() === "offered"}>
            <div class="summary-box"><FolderOpen size={21} /><div><strong>Vous gardez le contrôle</strong><p>Choisissez le dossier de ce lot et décidez quoi faire pour chaque fichier déjà présent.</p></div></div>
            <Show when={lastDirectory()}>{(directory) => <div class="remembered-folder"><div><span>Dernier dossier</span><strong>{directory().name || "Dossier mémorisé"}</strong></div><button onClick={() => void forgetDirectory()}>Oublier</button></div>}</Show>
            <Show when={folderNotice()}><p class="inline-notice" role="status">{folderNotice()}</p></Show>
            <div class="folder-actions">
              <Show when={lastDirectory()}>{(directory) => <button class="button primary" onClick={() => void useLastDirectory()}><FolderOpen size={19} /> Utiliser {directory().name || "ce dossier"}</button>}</Show>
              <button classList={{ primary: !lastDirectory(), secondary: Boolean(lastDirectory()), button: true }} onClick={() => void chooseDirectory()}><FolderOpen size={19} /> Choisir un autre dossier</button>
            </div>
          </Show>
          <Show when={phase() === "receiving"}>
            <div class="overall-progress"><div class="flex justify-between"><span>Progression totale</span><strong>{overall()} %</strong></div><div class="progress-track large"><div class="progress-fill" style={{ width: `${overall()}%` }} /></div></div>
            <button class="button ghost wide" onClick={() => { void transfer?.cancel(); setError("Le lot a été annulé."); setPhase("error"); }}>Annuler le transfert</button>
          </Show>
          <Show when={phase() === "done"}><div class="next-batch"><span class="pulse-dot status-live" /><div><strong>Connexion maintenue</strong><p>Le smartphone peut proposer un autre lot, vers ce dossier ou un autre.</p></div></div><button class="button ghost wide" onClick={() => void endSession()}><LogOut size={18} /> Terminer la session</button></Show>
        </section>
      </Show>

      <Show when={phase() === "error"}>
        <section class="session-card error-card"><div class="dialog-icon error"><X size={23} /></div><h1>Le lot s’est interrompu</h1><p>{error()}</p><button class="button primary" onClick={() => { setError(""); setPhase(peerStatus() === "connected" ? "done" : "waiting"); }}>Rester dans la session</button><button class="button ghost" onClick={() => void endSession()}>Terminer la session</button></section>
      </Show>
      <Show when={phase() === "ended"}><section class="session-card error-card"><div class="dialog-icon"><LogOut size={23} /></div><h1>Session terminée</h1><p>Le smartphone a fermé ce passage privé.</p><button class="button primary" onClick={reset}>Retour à l’accueil</button></section></Show>
      <PrivacyStrip />
      <Show when={collision()}>{(request) => <CollisionDialog request={request()} />}</Show>
    </main>
  );
};

const SenderView: Component<{ config: PublicConfig; roomId: string; secret: string }> = (props) => {
  const [phase, setPhase] = createSignal<SenderPhase>("connecting");
  const [prepared, setPrepared] = createSignal<readonly PreparedFile[]>([]);
  const [manifest, setManifest] = createSignal<TransferManifest>();
  const [detailed, setDetailed] = createSignal(true);
  const [preparing, setPreparing] = createSignal({ done: 0, total: 0 });
  const [progress, setProgress] = createSignal<Record<string, number>>({});
  const [error, setError] = createSignal("");
  const [signalingStatus, setSignalingStatus] = createSignal<SignalingStatus>("connecting");
  const [peerStatus, setPeerStatus] = createSignal<PeerStatus>("idle");
  const transfer = new SenderTransfer();
  const signaling = new SignalingClient(props.config.signalingUrl, props.roomId, props.secret, "sender");
  const peer = new LanPeer("sender", (payload) => signaling.send(payload));

  signaling.onStatus = setSignalingStatus;
  signaling.onError = (message) => { setError(message); setPhase("error"); };
  signaling.onSignal = (payload) => peer.handleSignal(payload);
  signaling.onPeerChange = (present) => { if (present && phase() === "connecting") setPhase("selecting"); };
  peer.onStatus = (status) => { setPeerStatus(status); if (status === "connected" && phase() === "connecting") setPhase("selecting"); };
  peer.onSessionEnded = () => { signaling.close(); setPhase("ended"); };
  peer.onChannel = (channel) => transfer.attach(channel);
  transfer.onProgress = (value) => { setProgress((current) => ({ ...current, [value.fileId]: value.transferred })); setPhase("sending"); };
  transfer.onComplete = () => setPhase("done");
  transfer.onError = (message) => { setError(message); setPhase("error"); };
  signaling.connect();
  onCleanup(() => { signaling.close(); peer.closePeer(); });

  const choose = async (list: FileList | null) => {
    const files = list ? [...list] : [];
    if (files.length === 0) return;
    setPhase("preparing");
    setPreparing({ done: 0, total: files.length });
    try {
      const value = await prepareFiles(files, (done, total) => setPreparing({ done, total }));
      setPrepared(value);
      setPhase("ready");
    } catch {
      setError("Certains fichiers n’ont pas pu être préparés."); setPhase("error");
    }
  };

  const propose = () => { setManifest(transfer.setPreparedFiles(prepared())); setPhase("proposed"); };
  const sendMore = () => {
    transfer.resetBatch();
    setPrepared([]);
    setManifest();
    setProgress({});
    setError("");
    setPreparing({ done: 0, total: 0 });
    setPhase("selecting");
  };
  const endSession = async () => {
    await peer.endSession().catch(() => undefined);
    signaling.close();
    setPhase("ended");
  };
  const offers = () => manifest()?.files ?? prepared().map(({ offer }) => offer);
  const total = () => offers().reduce((sum, file) => sum + file.size, 0);
  const overall = () => totalProgress(offers(), progress());

  return (
    <main class="page-shell sender-shell">
      <header class="topbar"><Brand /><HeaderActions signaling={signalingStatus()} peer={peerStatus()} /></header>
      <section class="session-card sender-card">
        <div class="phone-badge"><Smartphone size={19} /> Depuis ce smartphone</div>
        <Show when={phase() === "connecting"}><div class="center-state"><RefreshCw size={30} class="animate-spin" /><h1>Connexion au PC…</h1><p>Gardez la page du PC ouverte.</p></div></Show>
        <Show when={phase() === "selecting"}>
          <div class="session-heading"><span class="step-label">Étape 2 sur 2</span><h1>Que voulez-vous envoyer ?</h1><p>Sélectionnez des photos, vidéos ou documents depuis votre smartphone.</p></div>
          <label class="drop-zone"><input type="file" multiple onChange={(event) => void choose(event.currentTarget.files)} /><span class="drop-icon"><Send size={25} /></span><strong>Choisir des fichiers</strong><span>Photos et Fichiers · plusieurs éléments acceptés</span></label>
        </Show>
        <Show when={phase() === "preparing"}><div class="center-state"><RefreshCw size={30} class="animate-spin" /><h1>Préparation sécurisée…</h1><p>Aperçus : {preparing().done}/{preparing().total}</p></div></Show>
        <Show when={["ready", "proposed", "sending", "done"].includes(phase())}>
          <div class="session-heading left"><span class="step-label">{phase() === "ready" ? "Votre sélection" : phase() === "proposed" ? "En attente du PC" : phase() === "done" ? "Terminé" : "Envoi en cours"}</span><h1>{phase() === "done" ? "Fichiers envoyés." : `${offers().length} fichier${offers().length > 1 ? "s" : ""} prêt${offers().length > 1 ? "s" : ""}`}</h1><p>{formatBytes(total())} · aucune copie sur le serveur</p></div>
          <div class="list-toolbar"><strong>Contenu</strong><div class="density-toggle"><button classList={{ active: !detailed() }} onClick={() => setDetailed(false)}><List size={17} /></button><button classList={{ active: detailed() }} onClick={() => setDetailed(true)}><LayoutList size={17} /></button></div></div>
          <FileList files={offers()} detailed={detailed()} progress={progress()} />
          <Show when={phase() === "ready"}><button class="button primary wide" onClick={propose}>Proposer le transfert <ArrowRight size={19} /></button></Show>
          <Show when={phase() === "proposed"}><div class="summary-box"><QrCode size={21} /><div><strong>Vérification sur le PC</strong><p>Le destinataire voit maintenant le récapitulatif et choisit son dossier.</p></div></div></Show>
          <Show when={phase() === "sending"}><div class="overall-progress"><div class="flex justify-between"><span>Progression totale</span><strong>{overall()} %</strong></div><div class="progress-track large"><div class="progress-fill" style={{ width: `${overall()}%` }} /></div></div><button class="button ghost wide" onClick={() => { transfer.cancel(); setError("Le lot a été annulé."); setPhase("error"); }}>Annuler</button></Show>
          <Show when={phase() === "done"}><div class="next-batch"><Check size={20} /><div><strong>Le passage reste ouvert</strong><p>Vous pouvez envoyer un nouveau lot sans rescanner le QR code.</p></div></div><button class="button primary wide" onClick={sendMore}><Send size={18} /> Envoyer d’autres fichiers</button><button class="button ghost wide" onClick={() => void endSession()}><LogOut size={18} /> Terminer la session</button></Show>
        </Show>
        <Show when={phase() === "error"}><div class="center-state"><div class="dialog-icon error"><X size={23} /></div><h1>Lot interrompu</h1><p>{error()}</p><button class="button primary" onClick={sendMore}>Choisir d’autres fichiers</button><button class="button ghost" onClick={() => void endSession()}>Terminer la session</button></div></Show>
        <Show when={phase() === "ended"}><div class="center-state"><div class="dialog-icon"><LogOut size={23} /></div><h1>Session terminée</h1><p>Ce passage privé est maintenant fermé. Vous pouvez fermer cette page.</p></div></Show>
      </section>
      <PrivacyStrip />
    </main>
  );
};

const App: Component<{ config: PublicConfig }> = (props) => {
  const match = location.pathname.match(/^\/join\/([A-Za-z0-9_-]{20,32})\/?$/u);
  const secret = location.hash.slice(1);
  if (match?.[1] && /^[A-Za-z0-9_-]{40,48}$/u.test(secret)) return <SenderView config={props.config} roomId={match[1]} secret={secret} />;
  if (location.pathname !== "/") history.replaceState(null, "", "/");
  return <ReceiverView config={props.config} />;
};

export default App;
